import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraCombobox } from '@mono/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { opportunityApiConnect } from '../../lib/api/opportunityApi.connect';
/**
 * Pick a SAM.gov notice by searching notice titles or IDs (the same search as the RFI
 * workspace's notice picker). A notice ID that search does not return can still be entered
 * as typed. `suggested` notice IDs (e.g. the pursuit's linked notices) are offered first.
 */ export function NoticeCombobox({ label, value, onChange, suggested = [], disabled, testId }) {
    const [query, setQuery] = useState('');
    const debounced = useDebounce(query, 300);
    const search = useQuery({
        queryKey: [
            'notice-combobox-search',
            debounced.trim()
        ],
        enabled: debounced.trim().length > 1,
        queryFn: ()=>opportunityApiConnect.listOpportunities({
                search: debounced.trim(),
                limit: 8
            }),
        retry: false
    });
    const options = useMemo(()=>{
        const out = [];
        const seen = new Set();
        const add = (o)=>{
            if (!o.value || seen.has(o.value)) return;
            seen.add(o.value);
            out.push(o);
        };
        for (const o of search.data?.opportunities ?? [])add({
            value: o.noticeId,
            label: o.title || o.noticeId,
            description: [
                o.noticeId,
                o.organization?.name
            ].filter(Boolean).join(' · ')
        });
        for (const id of suggested)add({
            value: id,
            label: id,
            description: 'Linked to this pursuit'
        });
        if (value) add({
            value,
            label: value
        });
        return out;
    }, [
        search.data,
        suggested,
        value
    ]);
    return /*#__PURE__*/ _jsx(SpecraCombobox, {
        label: label,
        placeholder: "Search SAM.gov notices",
        searchPlaceholder: "Notice title or ID",
        emptyMessage: query.trim().length > 1 ? 'No matching notices.' : 'Type to search notices.',
        loading: search.isFetching,
        shouldFilter: false,
        searchValue: query,
        onSearchValueChange: setQuery,
        options: options,
        value: value || undefined,
        onValueChange: (v)=>onChange(v ?? ''),
        clearable: true,
        disabled: disabled,
        onCreate: (q)=>onChange(q.trim()),
        canCreate: (q, opts)=>/^[A-Za-z0-9][A-Za-z0-9_.-]{4,}$/.test(q.trim()) && !opts.some((o)=>o.value === q.trim()),
        createLabel: (q)=>`Use notice ID ${q.trim()}`,
        triggerProps: testId ? {
            'data-testid': testId
        } : undefined
    });
}
